import React, { useState, useEffect } from 'react';
import { useMutation } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { expertDashboardApi } from '../../../lib/http';
import { Schedule, WEEKDAY_NAMES } from '../../../types/working-hours';
import TimePicker from '../../ui/TimePicker';

interface ScheduleFormPanelProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  editSchedule: Schedule | null;
}

interface DayHours {
  enabled: boolean;
  ranges: { start: number; end: number }[];
}

const ScheduleFormPanel: React.FC<ScheduleFormPanelProps> = ({
  isOpen,
  onClose,
  onSuccess,
  editSchedule,
}) => {
  const [name, setName] = useState('');
  const [startsAt, setStartsAt] = useState('');
  const [endsAt, setEndsAt] = useState('');
  const [priority, setPriority] = useState(1);
  const [weeklyHours, setWeeklyHours] = useState<Map<number, DayHours>>(new Map());

  // Initialize form
  useEffect(() => {
    if (isOpen) {
      if (editSchedule) {
        setName(editSchedule.name);
        setStartsAt(editSchedule.starts_at || '');
        setEndsAt(editSchedule.ends_at || '');
        setPriority(editSchedule.priority);

        const hoursMap = new Map<number, DayHours>();
        for (let i = 1; i <= 7; i++) {
          hoursMap.set(i, { enabled: false, ranges: [] });
        }

        editSchedule.working_hours.forEach(wh => {
          const day = hoursMap.get(wh.weekday)!;
          day.enabled = true;
          day.ranges.push({ start: wh.start_min, end: wh.end_min });
        });

        setWeeklyHours(hoursMap);
      } else {
        setName('');
        setStartsAt('');
        setEndsAt('');
        setPriority(1);

        // Default: Mon-Fri 9-17
        const hoursMap = new Map<number, DayHours>();
        for (let i = 1; i <= 7; i++) {
          hoursMap.set(i, {
            enabled: i <= 5,
            ranges: i <= 5 ? [{ start: 540, end: 1020 }] : [],
          });
        }
        setWeeklyHours(hoursMap);
      }
    }
  }, [isOpen, editSchedule]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      const workingHoursAttrs: { weekday: number; start_min: number; end_min: number }[] = [];

      weeklyHours.forEach((day, weekday) => {
        if (day.enabled) {
          day.ranges.forEach(range => {
            workingHoursAttrs.push({
              weekday,
              start_min: range.start,
              end_min: range.end,
            });
          });
        }
      });

      if (editSchedule) {
        return expertDashboardApi.schedules.update(editSchedule.id, {
          name,
          starts_at: startsAt || undefined,
          ends_at: endsAt || undefined,
          priority,
          working_hours_attributes: workingHoursAttrs,
        });
      } else {
        return expertDashboardApi.schedules.create({
          name,
          starts_at: startsAt || undefined,
          ends_at: endsAt || undefined,
          priority,
          working_hours_attributes: workingHoursAttrs,
        });
      }
    },
    onSuccess: () => {
      toast.success(editSchedule ? 'Schedule updated' : 'Schedule created');
      onSuccess();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to save schedule');
    },
  });

  const handleDayToggle = (weekday: number) => {
    const newHours = new Map(weeklyHours);
    const day = newHours.get(weekday)!;
    day.enabled = !day.enabled;
    if (day.enabled && day.ranges.length === 0) {
      day.ranges = [{ start: 540, end: 1020 }];
    }
    setWeeklyHours(newHours);
  };

  const handleTimeChange = (weekday: number, rangeIndex: number, field: 'start' | 'end', minutes: number) => {
    const newHours = new Map(weeklyHours);
    const day = newHours.get(weekday)!;
    day.ranges[rangeIndex][field] = minutes;
    setWeeklyHours(newHours);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={onClose}>
      <div
        className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-gray-900">
              {editSchedule ? 'Edit Schedule' : 'New Seasonal Schedule'}
            </h2>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 rounded-lg"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Form */}
          <div className="space-y-6">
            {/* Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Schedule Name
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., Summer Hours, Holiday Schedule"
                className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              />
            </div>

            {/* Date Range */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Start Date
                </label>
                <input
                  type="date"
                  value={startsAt}
                  onChange={(e) => setStartsAt(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  End Date
                </label>
                <input
                  type="date"
                  value={endsAt}
                  onChange={(e) => setEndsAt(e.target.value)}
                  min={startsAt}
                  className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                />
              </div>
            </div>

            {/* Priority */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Priority (higher = takes precedence)
              </label>
              <input
                type="number"
                value={priority}
                onChange={(e) => setPriority(parseInt(e.target.value) || 1)}
                min={1}
                max={100}
                className="w-24 px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              />
            </div>

            {/* Weekly Hours */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Working Hours
              </label>
              <div className="space-y-3 bg-gray-50 rounded-xl p-4">
                {[1, 2, 3, 4, 5, 6, 7].map(weekday => {
                  const day = weeklyHours.get(weekday);
                  if (!day) return null;

                  return (
                    <div key={weekday} className="flex items-center gap-3">
                      <button
                        type="button"
                        onClick={() => handleDayToggle(weekday)}
                        className={`
                          relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full
                          border-2 border-transparent transition-colors duration-200
                          ${day.enabled ? 'bg-purple-600' : 'bg-gray-200'}
                        `}
                      >
                        <span
                          className={`
                            pointer-events-none inline-block h-4 w-4 transform rounded-full
                            bg-white shadow transition duration-200
                            ${day.enabled ? 'translate-x-4' : 'translate-x-0'}
                          `}
                        />
                      </button>
                      <span className="w-20 text-sm font-medium text-gray-700">
                        {WEEKDAY_NAMES[weekday]}
                      </span>
                      {day.enabled ? (
                        <div className="flex items-center gap-2">
                          <TimePicker
                            value={day.ranges[0]?.start || 540}
                            onChange={(m) => handleTimeChange(weekday, 0, 'start', m)}
                            max={1425}
                            aria-label={`${WEEKDAY_NAMES[weekday]} start time`}
                            className="px-2 py-1 flex items-center gap-1 border border-gray-300 rounded-lg text-sm font-medium focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                          />
                          <span className="text-gray-400">-</span>
                          <TimePicker
                            value={day.ranges[0]?.end || 1020}
                            onChange={(m) => handleTimeChange(weekday, 0, 'end', m)}
                            min={(day.ranges[0]?.start || 540) + 15}
                            aria-label={`${WEEKDAY_NAMES[weekday]} end time`}
                            className="px-2 py-1 flex items-center gap-1 border border-gray-300 rounded-lg text-sm font-medium focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                          />
                        </div>
                      ) : (
                        <span className="text-sm text-gray-400">Closed</span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="mt-8 flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={() => saveMutation.mutate()}
              disabled={!name || !startsAt || !endsAt || saveMutation.isPending}
              className="flex-1 px-4 py-2.5 bg-purple-600 text-white rounded-xl hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {saveMutation.isPending ? 'Saving...' : editSchedule ? 'Update Schedule' : 'Create Schedule'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScheduleFormPanel;
